import {
  Injectable,
  signal,
  inject,
  DestroyRef,
  computed
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { environment } from '@env';
import { AuthState } from '@app/modules/users';

import { BrowserApiService } from '../common';
import { getBrowserLanguage, syncWithLocalStorage } from '../helpers';

import { ApiResponse, CurrencyRates } from './currencies.entity';
import { CurrenciesService } from './currencies.service';
import { CURRENCY_SYMBOLS, guessCurrency } from './utils';

const REFRESH_RATE = 120 * 1000; // 2 minutes

@Injectable({
  providedIn: 'root'
})
export class CurrenciesState {
  private readonly currenciesService = inject(CurrenciesService);
  private readonly authState = inject(AuthState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = inject(BrowserApiService);

  public readonly base = signal<string>(environment.baseCurrency);
  public readonly userCurrency = signal<string>(environment.baseCurrency);
  public readonly updated = signal<number|null>(null);
  public readonly rates = signal<CurrencyRates>({
    [environment.baseCurrency]: 1
  });

  public readonly userCurrencySymbol = computed(() => this.getCurrencySymbol(this.userCurrency()));

  private readonly localStorageEffect = syncWithLocalStorage('currencies', {
    base: this.base,
    updated: this.updated,
    rates: this.rates
  });

  constructor() {
    if(this.isUpdated(this.updated()) || this.browser.isServer()) {
      return;
    }

    this.refreshRates();
  }

  getCurrencySymbol(currencyCode: string): string {
    return CURRENCY_SYMBOLS[currencyCode] || '';
  }

  private isUpdated(updatedAt: number|null): boolean {
    if(null === updatedAt) {
      return false;
    }

    return (Date.now() - updatedAt) < REFRESH_RATE;
  }
  
  private refreshRates(): void {
    this.currenciesService.getRates()
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe((response: ApiResponse.RatesList) => {
        const rates: Record<string, number> = {};

        for(const currency in response.rates) {
          rates[currency] = Number(response.rates[currency]);
        }

        this.base.set(response.base);
        this.updated.set(Date.now());
        this.rates.set(rates);
      });
  }
}